import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ProspectView } from "@joogoos/contracts";
import { api } from "../api.js";
import { Icon } from "../ui/icons.js";
import { CompanyPanel } from "./CompanyPanel.js";
import { ImportPanel } from "./ImportPanel.js";
import { when } from "./Today.js";

/**
 * **Customers → Companies** (23 §the screens).
 *
 * The whole list, and the only screen that is a list. It used to be three — Write showed what was ready, Follow
 * showed what had been sent, and this showed everything with filter chips named after the other two — so the
 * same rows were drawn three times and a person had to know the loop to know which screen to open.
 *
 * A row opens the company itself. Nothing here is a drill-down that ends somewhere else.
 */

/**
 * Each chip counts exactly the rows it shows, under a name true of every one of them. *Letter waiting* used to hold
 * everything not yet sent — the companies still being read and the ones judged with no letter written — and said
 * "Letter waiting 12" over twelve companies being read and no letter at all (the first pilot round, 2026-10-05). The
 * row's own word (`WORDS`, below) is what each chip is named after.
 */
const FILTERS = [
  ["all", "All"], ["reading", "Being read"], ["judged", "Judged"], ["waiting", "Letter waiting"], ["sent", "Written to"],
  ["answered", "Answered"], ["finished", "Finished"],
] as const;

const IN: Record<string, ProspectView["state"][]> = {
  reading: ["found"],
  judged: ["judged", "ready"],
  waiting: ["drafted", "approved"],
  sent: ["sent", "followed"],
  answered: ["replied", "meeting", "signed_in", "paying", "later"],
  finished: ["no_fit", "no_reply", "declined", "unsubscribed", "bounced", "suppressed"],
};

export function Companies() {
  const q = useQuery({
    queryKey: ["customers", "prospects"],
    queryFn: () => api.prospects(),
    // While anything is still being read, the list is worth asking for again: a verdict lands a few seconds later.
    refetchInterval: (query) => (query.state.data?.prospects ?? []).some((p) => p.state === "found") ? 4000 : false,
  });
  const [filter, setFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  // The other way companies arrive: a list of named people somebody brings (23 §a list somebody brings).
  const importButton = (
    <button
      onClick={() => setImporting(true)}
      className="px-2.5 py-1 rounded-lg border border-line-2 bg-page text-[11.5px] font-medium flex items-center gap-1.5"
    ><Icon name="upload" size={12} /> Import a list</button>
  );
  const importPanel = importing ? <ImportPanel onClose={() => setImporting(false)} /> : null;

  const d = q.data;
  if (q.isPending) return <p className="text-sm text-faint">…</p>;
  if (!d?.prospects.length) {
    return (
      <div className="flex flex-col gap-3 max-w-[560px]">
        <p className="text-[13px] text-muted leading-relaxed">
          No companies yet. The loop finds them once it is running, and <b>Find companies</b> on Today is how to
          look by hand. A list of named people you already have — a contact directory's export — comes in here.
        </p>
        <div className="self-start">{importButton}</div>
        {importPanel}
      </div>
    );
  }

  const words = search.trim().toLowerCase();
  const shown = d.prospects
    .filter((p) => filter === "all" || IN[filter]?.includes(p.state))
    .filter((p) => !words || p.companyName.toLowerCase().includes(words) || (p.domain ?? "").includes(words));

  return (
    <div className="flex flex-col gap-3 max-w-[1100px]">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-base font-semibold">Companies</h1>
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map(([code, label]) => (
            <button
              key={code} onClick={() => setFilter(code)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${filter === code ? "bg-ink text-white border-ink" : "bg-page border-line-2 text-ink-2"}`}
            >
              {label} {code === "all" ? d.counts.all : count(d.prospects, code)}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {importButton}
          <input
            value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" aria-label="Search"
            className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-page text-[13px] w-[190px]"
          />
        </div>
      </div>

      <div className="rounded-xl border border-line bg-panel overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-[.06em] text-muted text-left border-b border-line">
              <th className="px-3 py-2 font-semibold">Company</th>
              <th className="px-3 py-2 font-semibold">Who we look for</th>
              <th className="px-3 py-2 font-semibold">The judge</th>
              <th className="px-3 py-2 font-semibold">Where it is</th>
              <th className="px-3 py-2 font-semibold">Last touch</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((p) => (
              <tr key={p.id} onClick={() => setOpen(p.id)} className="border-b border-line last:border-0 cursor-pointer hover:bg-page">
                <td className="px-3 py-2">
                  <span className="flex flex-col">
                    <span className="font-semibold">{p.companyName}</span>
                    <span className="text-[11px] text-faint">
                      {p.contact ? <>{personOf(p.contact)} · </> : null}{p.domain ?? "no website"}
                    </span>
                  </span>
                </td>
                <td className="px-3 py-2 text-muted">{p.groupName ?? "—"}</td>
                <td className="px-3 py-2">
                  {p.verdict
                    ? <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-page border border-line-2 text-muted">{p.verdict} · {p.score}</span>
                    : <span className="text-[11.5px] text-faint">reading…</span>}
                </td>
                <td className="px-3 py-2"><State state={p.state} /></td>
                <td className="px-3 py-2 text-muted text-[12px]">{when(p.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!shown.length && <p className="text-[12.5px] text-faint">Nothing matches.</p>}

      {open && <CompanyPanel prospectId={open} onClose={() => setOpen(null)} />}
      {importPanel}
    </div>
  );
}

const count = (rows: ProspectView[], code: string) => rows.filter((p) => IN[code]?.includes(p.state)).length;

/** The person a list brought, as a row names them: their name and their role, whichever the list gave. */
export const personOf = (c: NonNullable<ProspectView["contact"]>): string =>
  [[c.firstName, c.lastName].filter(Boolean).join(" "), c.role].filter(Boolean).join(", ");

/** Where a company is, in the words the rest of the tab uses for it. */
const WORDS: Record<string, { says: string; tone: string }> = {
  found: { says: "reading", tone: "bg-line-2 text-ink-2" },
  judged: { says: "judged", tone: "bg-line-2 text-ink-2" },
  ready: { says: "ready to write", tone: "bg-line-2 text-ink-2" },
  drafted: { says: "letter waiting", tone: "bg-accent/10 text-accent" },
  approved: { says: "about to go", tone: "bg-accent/10 text-accent" },
  sent: { says: "sent", tone: "bg-line-2 text-ink-2" },
  followed: { says: "followed up", tone: "bg-line-2 text-ink-2" },
  replied: { says: "replied", tone: "bg-accent/10 text-accent" },
  meeting: { says: "a call booked", tone: "bg-ink text-white" },
  later: { says: "not now", tone: "bg-line-2 text-ink-2" },
  declined: { says: "said no", tone: "bg-line-2 text-ink-2" },
  no_fit: { says: "not a fit", tone: "bg-line-2 text-ink-2" },
  no_reply: { says: "no reply", tone: "bg-line-2 text-ink-2" },
  unsubscribed: { says: "asked to stop", tone: "bg-accent/10 text-accent" },
  bounced: { says: "bounced", tone: "bg-accent/10 text-accent" },
  suppressed: { says: "suppressed", tone: "bg-accent/10 text-accent" },
};

const State = ({ state }: { state: string }) => {
  const w = WORDS[state];
  return <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${w?.tone ?? "bg-line-2 text-ink-2"}`}>{w?.says ?? state}</span>;
};
